import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { selectDarkMode } from "../../../app/store/slices/uiSlice";
import CRUDListView from "./CRUDListView";
import CRUDDetailView from "./CRUDDetailView";
import { TableLoadingSkeleton } from "../ui";
import { useCRUDData } from "./useCRUDData";
import { useCRUDOperations } from "./useCRUDOperations";
import { useCRUDFilters } from "./useCRUDFilters";
import { useDebounce } from "../../hooks/useDebounce";
import { axiosInstance } from "../../services/api";

export default function CRUDManager({ config }) {
  const { title, endpoint, crudEndpoint, storageKey, dataUrl, icon: Icon, columns = [], formFields: rawFormFields = [], searchFields = [], onDataChange, customHandlers = {}, useUrlNavigation = false, basePath = "" } = config;

  const navigate = useNavigate();
  const location = useLocation();
  const formFields = Array.isArray(rawFormFields) ? rawFormFields : [];
  const apiEndpoint = endpoint || storageKey;

  const [activeTab, setActiveTab] = useState(config.formState?.activeTab || "basic");
  const [customModal, setCustomModal] = useState({ isOpen: false, type: null, item: null, data: {} });
  const [customFiltersWithOptions, setCustomFiltersWithOptions] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  const { data, loading, error, availableStatuses, statistics, createItem, updateItem, deleteItem, setData, refreshData, pagination, handlePageChange, handleLimitChange, searchData, filterByStatus, statusFilter, customFilterValues, handleCustomFilterChange, handleServerSort, sortBy: serverSortBy, sortOrder: serverSortOrder } = useCRUDData(
    apiEndpoint,
    { dataUrl, storageKey, onDataChange, statusFilterKey: config.statusFilterKey, crudEndpoint, customFiltersConfig: config.customFilters, customHandlers: { ...customHandlers, statusOptionsLoader: config.statusOptionsLoader } },
  );

  const { viewState, confirmationModal, isSubmitting, isDeleting, toast, setConfirmationModal, handleAdd, handleView, handleEdit, handleDelete, confirmDelete, handleBackToList, handleSubmit, handleEditFromView, handleCancelEdit } = useCRUDOperations(
    apiEndpoint,
    data,
    formFields,
    customHandlers,
    setData,
    deleteItem,
    createItem,
    updateItem,
    refreshData,
    {},
    title,
  );

  // Success message state for navigation-based success messages
  const [successMessage, setSuccessMessage] = useState(null);

  // URL-based navigation handlers
  const handleUrlView = (item) => {
    if (useUrlNavigation && basePath) {
      navigate(`${basePath}/view/${item.id}`);
    } else {
      handleView(item);
    }
  };

  const handleUrlEdit = (item) => {
    if (useUrlNavigation && basePath) {
      navigate(`${basePath}/edit/${item.id}`);
    } else {
      handleEdit(item);
    }
  };

  const handleUrlAdd = () => {
    if (useUrlNavigation && basePath) {
      navigate(`${basePath}/add`);
    } else {
      handleAdd();
    }
  };

  // If all records fit in one page they're already in memory — sort client-side, skip the API call
  const allDataFitsInPage = pagination?.total > 0 && data.length >= pagination.total;

  // Client-side sort state (used only when allDataFitsInPage)
  const [clientSortBy, setClientSortBy] = useState(null);
  const [clientSortOrder, setClientSortOrder] = useState('asc');

  const { searchQuery, currentPage, totalItems, totalPages, startIndex, endIndex, currentItems: filterItems, setCurrentPage, handleSearchChange } = useCRUDFilters(data, columns, searchFields);

  // When all data is in memory, sort it locally; otherwise use server-returned order
  const currentItems = allDataFitsInPage && clientSortBy
    ? [...data].sort((a, b) => {
        const aVal = (a[clientSortBy] ?? '').toString().toLowerCase();
        const bVal = (b[clientSortBy] ?? '').toString().toLowerCase();
        const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        return clientSortOrder === 'asc' ? cmp : -cmp;
      })
    : filterItems;

  const activeSortBy = allDataFitsInPage ? (clientSortBy || serverSortBy) : serverSortBy;
  const activeSortOrder = allDataFitsInPage ? (clientSortBy ? clientSortOrder : serverSortOrder) : serverSortOrder;

  const handleSmartSort = useCallback((field) => {
    if (allDataFitsInPage) {
      // Toggle direction if same field, else reset to asc — no API call
      setClientSortOrder(prev => clientSortBy === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
      setClientSortBy(field);
    } else {
      handleServerSort(field);
    }
  }, [allDataFitsInPage, clientSortBy, handleServerSort]);

  // Debounce search query for server-side search (500ms delay)
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const previousSearchRef = useRef(debouncedSearchQuery);
  const isInitialMount = useRef(true);
  const lastRefreshTimestamp = useRef(null);

  // Detect refresh state from navigation (e.g., after successful edit in CRUDDetailPage)
  useEffect(() => {
    if (location.state?.refresh && location.state?.timestamp) {
      // Only refresh if this is a new timestamp (avoid duplicate refreshes)
      if (lastRefreshTimestamp.current !== location.state.timestamp) {
        lastRefreshTimestamp.current = location.state.timestamp;
        refreshData();
        // Show success message if provided
        if (location.state.successMessage) {
          setSuccessMessage(location.state.successMessage);
          setTimeout(() => setSuccessMessage(null), 2000);
        }
        // Clear the state to prevent re-triggering on subsequent renders
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, refreshData, navigate, location.pathname, apiEndpoint]);

  // Trigger server-side search ONLY when debounced search query value changes
  useEffect(() => {
    // Skip on initial mount (data is already fetched by useCRUDData)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Only call searchData if the search query actually changed
    if (previousSearchRef.current !== debouncedSearchQuery) {
      previousSearchRef.current = debouncedSearchQuery;
      if (searchData) {
        searchData(debouncedSearchQuery);
      }
    }
  }, [debouncedSearchQuery, searchData]);

  // Load custom filter options
  useEffect(() => {
    const loadCustomFilterOptions = async () => {
      if (!config.customFilters || config.customFilters.length === 0) return;

      const filtersWithOptions = await Promise.all(
        config.customFilters.map(async (filter) => {
          if (filter.optionsLoader) {
            try {
              const options = await filter.optionsLoader();
              return { ...filter, options };
            } catch (err) {
              console.error(`Failed to load options for filter ${filter.key}:`, err);
              return { ...filter, options: [] };
            }
          }
          return filter;
        }),
      );
      setCustomFiltersWithOptions(filtersWithOptions);
    };

    loadCustomFilterOptions();
  }, [config.customFilters]);

  const openCustomModal = (type, item) => {
    setCustomModal({ isOpen: true, type, item, data: {} });
  };

  const closeCustomModal = () => {
    setCustomModal({ isOpen: false, type: null, item: null, data: {} });
  };

  const handleCustomModalSubmit = (modalData) => {
    if (config.customModals && config.customModals[customModal.type]) {
      const modalConfig = config.customModals[customModal.type];

      if (modalConfig.onSubmit) {
        modalConfig.onSubmit(modalData, customModal.item, (updatedItem) => {
          const updatedData = data.map((item) => (item.id === updatedItem.id ? updatedItem : item));
          setData(updatedData); // ✅ FIXED
        });
      }
    }
    closeCustomModal();
  };

  const getDisplayName = (item) => {
    if (!item) return "";
    return item.parentName || item.name || item.centerName || item.centers || item.taluka || item.district || item.village || item.childName || `${title} #${item.id}`;
  };

const handleExport = async (format = 'EXCEL') => {
  setIsExporting(true);
  try {
    const separator = apiEndpoint.includes("?") ? "&" : "?";
    let exportUrl = `${apiEndpoint}${separator}export=true&exportFormat=${format}`;

    if (searchQuery) exportUrl += `&search=${encodeURIComponent(searchQuery)}`;
    if (statusFilter && statusFilter !== 'all') {
      exportUrl += `&status=${encodeURIComponent(statusFilter.toUpperCase())}`;
    }

    Object.entries(customFilterValues || {}).forEach(([key, val]) => {
      if (val && val !== 'all') {
        exportUrl += `&${key}=${encodeURIComponent(val)}`;
      }
    });

    const response = await axiosInstance.get(exportUrl, {
      responseType: 'blob',
    });

    const contentType = response.headers['content-type'] || '';
    const isCSV = contentType.includes('text/csv') || format === 'CSV';

    const blob = new Blob([response.data], {
      type: isCSV
        ? 'text/csv'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const disposition = response.headers['content-disposition'];
    const fileNameMatch = disposition?.match(/filename="(.+)"/);
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const moduleName = (storageKey || title || 'export').replace(/\s+/g, '-').toLowerCase();
    const fileName = fileNameMatch
      ? fileNameMatch[1]
      : isCSV ? `${moduleName}-export-${timestamp}.csv` : `${moduleName}-export-${timestamp}.xlsx`;

    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    window.URL.revokeObjectURL(url);

  } catch (err) {
    console.error('Export failed:', err);
  } finally {
    setIsExporting(false);
  }
};

  const darkMode = useSelector(selectDarkMode);

  // Show skeleton loader while loading
  if (loading && data.length === 0) {
    return <TableLoadingSkeleton title={title} rows={6} columns={columns.length || 4} showSearch={true} showFilters={true} showAddButton={true} />;
  }

  // Error state with dark mode support
  if (error) {
    return (
      <div className="p-6">
        <div className={`p-4 rounded-xl border ${darkMode ? "bg-red-900/20 border-red-800 text-red-400" : "bg-red-50 border-red-200 text-red-600"}`}>
          <p className="font-medium">Error loading {title.toLowerCase()}s</p>
          <p className="text-sm mt-1 opacity-80">{error}</p>
        </div>
      </div>
    );
  }

  if (viewState.mode === "list") {
    return (
      <CRUDListView
        title={title}
        Icon={Icon}
        columns={columns}
        currentItems={currentItems}
        searchQuery={searchQuery}
        filterStatus={statusFilter}
        availableStatuses={availableStatuses}
        statistics={statistics}
        statsConfig={config.statsConfig}
        sortBy={activeSortBy}
        sortOrder={activeSortOrder}
        totalItems={pagination?.total || totalItems}
        startIndex={startIndex}
        endIndex={endIndex}
        currentPage={pagination?.page || currentPage}
        totalPages={pagination?.totalPages || totalPages}
        config={config}
        confirmationModal={confirmationModal}
        customModal={customModal}
        successMessage={successMessage}
        onSearchChange={handleSearchChange}
        onFilterChange={filterByStatus}
        onSort={handleSmartSort}
        onAdd={handleUrlAdd}
        onView={handleUrlView}
        onEdit={handleUrlEdit}
        onDelete={handleDelete}
        onPageChange={pagination?.totalPages > 1 ? handlePageChange : setCurrentPage}
        onConfirmDelete={confirmDelete}
        onCancelDelete={() => setConfirmationModal({ isOpen: false, item: null })}
        closeCustomModal={closeCustomModal}
        handleCustomModalSubmit={handleCustomModalSubmit}
        setCustomModal={setCustomModal}
        isDeleting={isDeleting}
        toast={toast}
        getDisplayName={getDisplayName}
        openCustomModal={openCustomModal}
        serverPagination={pagination}
        isLoading={loading}
        renderExpandedRow={customHandlers?.renderExpandedRow}
        customFilters={customFiltersWithOptions}
        customFilterValues={customFilterValues}
        onCustomFilterChange={handleCustomFilterChange}
        navigate={navigate}
        onLimitChange={handleLimitChange}
        onExport={handleExport}
        isExporting={isExporting}
      />
    );
  }

  return (
    <CRUDDetailView
      title={title}
      viewState={viewState}
      formFields={formFields}
      error={error}
      isSubmitting={isSubmitting}
      isDeleting={isDeleting}
      toast={toast}
      customHandlers={customHandlers}
      activeTab={activeTab}
      onBackToList={handleBackToList}
      onEditFromView={handleEditFromView}
      onDelete={handleDelete}
      onSubmit={handleSubmit}
      onCancel={handleCancelEdit}
      setActiveTab={setActiveTab}
      confirmationModal={confirmationModal}
      onConfirmDelete={confirmDelete}
      onCancelDelete={() => setConfirmationModal({ isOpen: false, item: null })}
      getDisplayName={getDisplayName}
    />
  );
}
